/**
 * ComparisonAnchor — persistent "current vs recommended" anchor.
 *
 * Reframed from SWOT columns into a narrative flow:
 * - What you have now
 * - What changes with each option
 * - Why the engine prefers it
 * - Trade-offs
 */

import type { SystemOptionCard } from './types';
import type { ScopeSnapshot } from './scopeModel';

function getDisplayedScore(option: SystemOptionCard): string {
  const score = option.scoreBreakdown?.overallScore ?? option.suitabilityScore;
  if (typeof score !== 'number' || !Number.isFinite(score)) return '—';
  return String(Math.round(Math.max(0, Math.min(100, score))));
}

function getTopReasons(option: SystemOptionCard, limit = 4): string[] {
  const rowLabels = (option.scoreBreakdown?.details?.breakdownRows ?? [])
    .filter((row) => row.scoreFull !== null && Number.isFinite(row.scoreFull))
    .sort((a, b) => (b.scoreFull ?? 0) - (a.scoreFull ?? 0))
    .map((row) => row.label || row.key.replace(/_/g, ' '));

  const reasons = option.scoreBreakdown?.details?.reasons ?? [];
  const merged = [...option.becauseBullets, ...rowLabels, ...reasons]
    .map((item) => item.trim())
    .filter(Boolean);

  return [...new Set(merged)].slice(0, limit);
}

function normaliseOptimisationLanguage(line: string): string {
  const lower = line.toLowerCase();
  if (lower.includes('load-comp') || (lower.includes('load') && lower.includes('control'))) {
    return 'Install load-compensating control to prevent short cycling and unlock real efficiency gains.';
  }
  return line;
}

interface Props {
  currentSystem: {
    label: string;
    strengths: string[];
    weaknesses: string[];
    threats: string[];
  };
  recommended: SystemOptionCard | null;
  recommendedB?: SystemOptionCard | null;
  scope: ScopeSnapshot | null;
}

function OptionColumn({ option, roleLabel }: { option: SystemOptionCard; roleLabel: string }) {
  const topReasons = getTopReasons(option);
  const practicalChanges = option.becauseBullets.slice(0, 4);
  const tradeOffs = [...option.wontSolve, ...option.wouldNeedToChange]
    .map(normaliseOptimisationLanguage)
    .slice(0, 4);

  return (
    <div className="comparison-anchor__side comparison-anchor__side--recommended">
      <div className="comparison-anchor__side-header">
        <span className="comparison-anchor__side-label">{roleLabel}</span>
        <span className="comparison-anchor__side-name">{option.label}</span>
        <span className={`hub-tile__badge hub-tile__badge--${option.confidence}`} style={{ marginLeft: 8 }}>
          {getDisplayedScore(option)} match
        </span>
      </div>

      <div className="comparison-anchor__quadrant comparison-anchor__quadrant--change">
        <span className="comparison-anchor__quadrant-label">What changes with {roleLabel}</span>
        <ul className="comparison-anchor__list">
          {practicalChanges.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      </div>

      {topReasons.length > 0 && (
        <div className="comparison-anchor__quadrant comparison-anchor__quadrant--strength">
          <span className="comparison-anchor__quadrant-label">Why the engine prefers this option</span>
          <ul className="comparison-anchor__list">
            {topReasons.map((reason, i) => <li key={i}>{reason}</li>)}
          </ul>
        </div>
      )}

      {tradeOffs.length > 0 && (
        <div className="comparison-anchor__quadrant comparison-anchor__quadrant--tradeoffs">
          <span className="comparison-anchor__quadrant-label">Trade-offs</span>
          <ul className="comparison-anchor__list">
            {tradeOffs.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {(option.assumptions.length > 0 || option.wouldNeedToChange.length > 0) && (
        <details className="comparison-anchor__why-toggle">
          <summary>Why?</summary>
          {option.assumptions.length > 0 && (
            <div className="comparison-anchor__quadrant comparison-anchor__quadrant--assumption">
              <span className="comparison-anchor__quadrant-label">Assumptions</span>
              <ul className="comparison-anchor__list">
                {option.assumptions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
        </details>
      )}
    </div>
  );
}

export function ComparisonAnchor({ currentSystem, recommended, recommendedB, scope }: Props) {
  const currentSnapshot = [...currentSystem.strengths, ...currentSystem.weaknesses, ...currentSystem.threats].slice(0, 5);

  return (
    <div className="comparison-anchor">
      <div className="comparison-anchor__mission">
        <strong>Every heating solution works — in the right house.</strong>
        <p>
          This assessment ranks options using your pipework, pressure, demand pattern,
          emitter capability, and upgrade tolerance.
        </p>
      </div>

      <div className="comparison-anchor__grid">
        <div className="comparison-anchor__side comparison-anchor__side--current">
          <div className="comparison-anchor__side-header">
            <span className="comparison-anchor__side-label">Current system</span>
            <span className="comparison-anchor__side-name">{currentSystem.label || 'Not specified'}</span>
          </div>

          {currentSnapshot.length > 0 && (
            <div className="comparison-anchor__quadrant comparison-anchor__quadrant--current">
              <span className="comparison-anchor__quadrant-label">What you have now</span>
              <ul className="comparison-anchor__list">
                {currentSnapshot.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
        </div>

        {recommended ? (
          <OptionColumn option={recommended} roleLabel="Option A" />
        ) : (
          <div className="comparison-anchor__side comparison-anchor__side--recommended">
            <div className="comparison-anchor__side-header">
              <span className="comparison-anchor__side-label">Option A</span>
              <span className="comparison-anchor__side-name comparison-anchor__side-name--empty">
                Select a target system from the options below
              </span>
            </div>
          </div>
        )}

        {recommendedB && (
          <OptionColumn option={recommendedB} roleLabel="Option B" />
        )}
      </div>

      {scope && (
        <div className="comparison-anchor__scope-strip">
          <div className="comparison-anchor__scope-col">
            <span className="comparison-anchor__scope-label">Included</span>
            <span className="comparison-anchor__scope-count">
              {scope.autoScope.length + scope.selectedScope.length} items
            </span>
          </div>
          <div className="comparison-anchor__scope-col">
            <span className="comparison-anchor__scope-label">Excluded</span>
            <span className="comparison-anchor__scope-count">
              {scope.excludedScope.length} items
              {scope.excludedScope.some(i => i.tradeOffWarning) && (
                <span className="comparison-anchor__scope-warning"> (trade-offs apply)</span>
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
